import { h, Component, ComponentType } from "preact";

import { Keyboard } from "../../foundation/enums";
import BasicRecord from "../../foundation/utils/BasicRecord";
import classname from "../../foundation/utils/classname";
import noderef from "../../foundation/utils/noderef";
import { getFirstScrollableParent, clearNativeSelection } from "../../foundation/utils/platform";
import { rectContainsPoint } from "../../foundation/utils/rectangle";

import Portal from "../Portal";
import PureComponent from "../PureComponent";

import { DragDropContainerState, DropTargetItem, DragPosition } from "./types";

import "./style.css";

interface ItemsProps<T = unknown> {
    class?: string;
    items: preact.ComponentChildren;
    dragDisabled?: boolean;
    focusable?: boolean;
    baseRef?: (el: HTMLElement | null) => void;
    onScroll?: (ev: Event) => void;
    onStartDrag?: (args: {
        draggingItemType: string;
        sourceRect: DOMRect;
        mirrorComponent: ComponentType;
        mirrorZIndex: number;
        dragData: T;
        dragOffset: DragPosition;
        position: DragPosition;
    }) => void;
    onDrag?: (position: DragPosition) => void;
    onEndDrag?: (canceled?: boolean) => void;
    registerDropTarget?: (dropTarget: DropTargetItem) => void;
    unregisterDropTarget?: (dropTarget: DropTargetItem) => void;
}

/**
 * Used for preventing rerendering of the items when mirror position changed
 */
class Items<T = unknown> extends PureComponent<ItemsProps<T>> {
    override getChildContext() {
        const {
            dragDisabled,
            onStartDrag,
            onDrag,
            onEndDrag,
            registerDropTarget,
            unregisterDropTarget
        } = this.props;

        return {
            dragDisabled,
            onStartDrag,
            onDrag,
            onEndDrag,
            registerDropTarget,
            unregisterDropTarget
        };
    }

    render() {
        const {
            class: cssClass,
            items,
            focusable,
            baseRef,
            onScroll
        } = this.props;

        const tabIndex = focusable ? 0 : undefined;

        return (
            <div class={cssClass} ref={baseRef} tabIndex={tabIndex} onScroll={onScroll}>
                {items}
            </div>
        );
    }
}

function calculateDragPosition(position: DragPosition) {
    return {
        top: position.top + window.scrollY,
        left: position.left
    };
}

interface DragDropContainerProps<T = unknown> {
    class?: string;
    itemsClass?: string;
    scrollableAreaOffset?: number;
    // Flags
    disabled?: boolean;
    noScroll?: boolean;
    focusable?: boolean;
    enableDropOntoMultipleTargets?: boolean;
    discardScrollableParents?: boolean; /** Disables auto scroll parent. */
    cancelOnEsc?: boolean;
    // Refs
    getScrollableDropContainers?: () => HTMLElement[] | null; /** Called when the drag operation started on the container */
    baseRef?: (el: HTMLElement | null) => void;
    // Callbacks
    onStartDrag?: (draggingItemType: string, dragData: T) => void;
    onEndDrag?: () => void;
    onScroll?: (ev: Event) => void;
    onCanScroll?: (canScroll: boolean) => void;
}

const SCROLL_STEP_MULTIPILIER = 0.2; // Multipilier for the scroll step size
const SCROLL_STEP_DISTANCE = 8; // Distance for direct scrolling with buttons
const SCROLLABLE_AREA_RATIO = 0.2; // Height ratio of the container for the (top and bottom) scrollable areas

class DragDropContainer<T = unknown> extends Component<DragDropContainerProps<T>, DragDropContainerState> {
    override state: DragDropContainerState = {
        dragging: false,
        draggingItemType: "",
        dragData: null,
        sourceRect: null,
        mirrorComponent: null,
        mirrorZIndex: undefined,
        mousePosition: null,
        dragOffset: {
            top: 0,
            left: 0
        },
        mirrorPosition: {
            top: 0,
            left: 0
        },
        dragCanceled: false
    };

    scrollableDropContainers: HTMLElement[] | null = null;
    lastScrolledDropContainer: HTMLElement | null = null;
    registeredDropTargets = new Set<DropTargetItem>();
    nodeRefs: BasicRecord<HTMLElement | null> = {};
    scrollRootAF: ReturnType<typeof requestAnimationFrame> | null = null;
    resizeObserver: ResizeObserver | null = null;

    findScrollContainer = () => {
        const { base } = this.nodeRefs;
        const { mirrorPosition } = this.state;
        const { discardScrollableParents } = this.props;

        // Find the container to be scrolled which contains the origin point.
        // If a container contains the point, update the lastScrolledDropContainer
        // to keep scrolling if container or window boundaries exceeded.
        // If no drop containers provided, use base as the container.
        let scrollContainer = this.lastScrolledDropContainer || base!;

        if (
            !discardScrollableParents &&
            (
                scrollContainer.scrollWidth <= scrollContainer.clientWidth ||
                scrollContainer.scrollHeight <= scrollContainer.clientHeight
            )
        ) {
            scrollContainer = getFirstScrollableParent(scrollContainer) || scrollContainer;
        }

        if (this.scrollableDropContainers) {
            for (const container of this.scrollableDropContainers) {
                const containerRect = container.getBoundingClientRect();
                const mouseOrigin = { x: mirrorPosition.left, y: mirrorPosition.top };
                if (rectContainsPoint(containerRect, mouseOrigin)) {
                    scrollContainer = container;
                    this.lastScrolledDropContainer = container;
                    break;
                }
            }
        }

        return scrollContainer;
    };

    scrollRoot = () => {
        const { mousePosition } = this.state;
        const { scrollableAreaOffset } = this.props;

        cancelAnimationFrame(this.scrollRootAF!); // Debounce in animation frames

        if (mousePosition === null) {
            // mousePosition should be defined to calculate scroll
            return;
        }

        const scrollContainer = this.findScrollContainer();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Height of the (top and bottom) scrollable areas. When mouse enters into those areas scrolling starts
        const scrollableHeight = scrollableAreaOffset ?? (containerRect.height * SCROLLABLE_AREA_RATIO);
        const topScrollOffset = mousePosition.y - scrollableHeight - containerRect.top;
        const shouldScrollToTop = topScrollOffset < 0 && scrollContainer.scrollTop > 0;

        if (shouldScrollToTop) {
            scrollContainer.scrollTop += topScrollOffset * SCROLL_STEP_MULTIPILIER;
            this.scrollRootAF = requestAnimationFrame(this.scrollRoot);
        }

        const bottomScrollOffset = mousePosition.y + scrollableHeight - containerRect.bottom;
        const shouldScrollToBottom = bottomScrollOffset > 0 &&
            scrollContainer.scrollTop + containerRect.height < scrollContainer.scrollHeight;

        if (shouldScrollToBottom) {
            scrollContainer.scrollTop += bottomScrollOffset * SCROLL_STEP_MULTIPILIER;
            this.scrollRootAF = requestAnimationFrame(this.scrollRoot);
        }

        const scrollableWidth = scrollableAreaOffset ?? (containerRect.width * SCROLLABLE_AREA_RATIO);
        const leftScrollOffset = mousePosition.x - scrollableWidth - containerRect.left;
        const shouldScrollToLeft = leftScrollOffset < 0 && scrollContainer.scrollLeft > 0;

        if (shouldScrollToLeft) {
            scrollContainer.scrollLeft += leftScrollOffset * SCROLL_STEP_MULTIPILIER;
            this.scrollRootAF = requestAnimationFrame(this.scrollRoot);
        }

        const rightScrollOffset = mousePosition.x + scrollableWidth - containerRect.right;
        const shouldScrollToRight = rightScrollOffset > 0 &&
            scrollContainer.scrollLeft + containerRect.width < scrollContainer.scrollWidth;

        if (shouldScrollToRight) {
            scrollContainer.scrollLeft += rightScrollOffset * SCROLL_STEP_MULTIPILIER;
            this.scrollRootAF = requestAnimationFrame(this.scrollRoot);
        }
    };

    scrollLeft = () => {
        const scrollContainer = this.findScrollContainer();
        scrollContainer.scrollLeft -= SCROLL_STEP_DISTANCE;
    };

    scrollRight = () => {
        const scrollContainer = this.findScrollContainer();
        scrollContainer.scrollLeft += SCROLL_STEP_DISTANCE;
    };

    scrollToEnd = () => {
        const scrollContainer = this.findScrollContainer();
        scrollContainer.scrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    };

    registerDropTarget = (dropTarget: DropTargetItem) => {
        this.registeredDropTargets.add(dropTarget);
    };

    unregisterDropTarget = (dropTarget: DropTargetItem) => {
        this.registeredDropTargets.delete(dropTarget);
    };

    checkHoverOnVisibleTargets = () => {
        const dragPosition = {
            top: this.state.mirrorPosition.top - window.scrollY,
            left: this.state.mirrorPosition.left
        };

        // Signals visible drop targets to handle drag action
        this.registeredDropTargets.forEach(dropTarget => {
            dropTarget.checkHover(dragPosition, this.state);
        });
    };

    checkDropOnVisibleTargets = () => {
        const endPosition = {
            top: this.state.mirrorPosition.top - window.scrollY,
            left: this.state.mirrorPosition.left
        };

        let dropped = false;
        this.registeredDropTargets.forEach(dropTarget => {
            if (this.props.enableDropOntoMultipleTargets || !dropped) {
                // If dropping onto multiple drop targets enabled, let targets handle this case on their onDrop callbacks
                // Otherwise allow only dropping onto a single target.
                dropped = dropTarget.checkDrop(endPosition, this.state);
            }

            dropTarget.checkHoverOut(this.state); // Trigger hover out for all visible drop targets if they have hover
        });
    };

    isDragEnded = (prevState: DragDropContainerState) => prevState.dragging && !this.state.dragging;

    isDragPositionChanged = (prevState: DragDropContainerState) => this.state.dragging &&
        this.state.mirrorPosition !== prevState.mirrorPosition;

    handleDocumentClick = (ev: MouseEvent) => {
        if (this.state.dragging) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
        }
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        const { cancelOnEsc } = this.props;
        if (cancelOnEsc && ev.key === Keyboard.Key.Escape) {
            ev.preventDefault();
            this.handleEndDrag(true);
        }
    };

    handleStartDrag = ({
        draggingItemType,
        sourceRect,
        mirrorComponent,
        mirrorZIndex,
        dragData,
        dragOffset,
        position
    }: {
        draggingItemType: string;
        sourceRect: DOMRect;
        mirrorComponent: ComponentType;
        mirrorZIndex: number;
        dragData: T;
        dragOffset: DragPosition;
        position: DragPosition;
    }) => {
        const { disabled, getScrollableDropContainers } = this.props;
        if (disabled) {
            return;
        }

        document.addEventListener("keydown", this.handleKeyDown);
        this.lastScrolledDropContainer = null;
        this.scrollableDropContainers = getScrollableDropContainers ? getScrollableDropContainers() : null;
        this.setState({
            dragging: true,
            draggingItemType,
            sourceRect,
            mirrorComponent,
            mirrorZIndex,
            dragData,
            dragOffset,
            mirrorPosition: calculateDragPosition(position)
        }, () => {
            document.body.classList.add("dragging");
            if (this.props.onStartDrag) {
                this.props.onStartDrag(draggingItemType, dragData);
            }
        });

        clearNativeSelection();
    };

    handleDrag = (position: DragPosition) => {
        if (this.props.disabled) {
            return;
        }

        this.setState({
            mirrorPosition: calculateDragPosition(position),
            mousePosition: {
                x: position.left,
                y: position.top
            }
        });
    };

    handleEndDrag = (canceled = false) => {
        if (this.props.disabled) {
            return;
        }

        document.removeEventListener("keydown", this.handleKeyDown);
        this.scrollableDropContainers = null;
        this.setState({
            dragging: false,
            mousePosition: null,
            dragCanceled: canceled
        }, () => {
            document.body.classList.remove("dragging");
            if (this.props.onEndDrag) {
                this.props.onEndDrag();
            }
        });

        clearNativeSelection();
    };

    handleBaseRef = (el: HTMLElement | null) => {
        noderef(this, "base")(el);
        this.props.baseRef?.(el);
    };

    handleWheel = () => {
        if (!this.state.dragging) {
            return;
        }

        // Updates `mirrorPosition` reference in state; so that `isDragPositionChanged` returns true
        this.setState(state => ({
            mirrorPosition: {
                top: state.mirrorPosition.top,
                left: state.mirrorPosition.left
            }
        }));
    };

    override componentDidMount() {
        document.addEventListener("click", this.handleDocumentClick, true);
        document.addEventListener("wheel", this.handleWheel);

        const { onCanScroll } = this.props;
        if (onCanScroll) {
            const scrollContainer = this.findScrollContainer();

            this.resizeObserver = new ResizeObserver(() => {
                onCanScroll(scrollContainer.scrollWidth > scrollContainer.clientWidth);
            });

            this.resizeObserver.observe(scrollContainer);
        }
    }

    override componentDidUpdate(_: DragDropContainerProps, prevState: DragDropContainerState) {
        if (this.isDragEnded(prevState)) {
            if (this.state.dragCanceled) {
                this.setState({ dragCanceled: false });
            } else {
                this.checkDropOnVisibleTargets();
            }

            return;
        }

        if (this.isDragPositionChanged(prevState)) {
            cancelAnimationFrame(this.scrollRootAF!);
            this.scrollRootAF = requestAnimationFrame(this.scrollRoot);
            this.checkHoverOnVisibleTargets();
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("click", this.handleDocumentClick, true);
        document.removeEventListener("wheel", this.handleWheel);
        document.body.classList.remove("dragging");
        this.resizeObserver?.disconnect();
    }

    render() {
        const {
            class: cssClass,
            children,
            disabled,
            itemsClass,
            noScroll,
            onScroll,
            focusable
        } = this.props;

        const {
            dragging,
            sourceRect,
            mirrorComponent,
            mirrorZIndex,
            dragData,
            mirrorPosition,
            dragOffset
        } = this.state;

        const itemsClasses = classname("dragDropItems", itemsClass, { noScroll });

        return (
            <div class={classname("dragDropContainer", cssClass)}>
                <Items
                    class={itemsClasses}
                    dragDisabled={disabled}
                    baseRef={this.handleBaseRef}
                    registerDropTarget={this.registerDropTarget}
                    unregisterDropTarget={this.unregisterDropTarget}
                    items={children}
                    focusable={focusable}
                    onScroll={onScroll}
                    onStartDrag={this.handleStartDrag}
                    onDrag={this.handleDrag}
                    onEndDrag={this.handleEndDrag}/>
                {dragging && (
                    <Portal>
                        <div
                            class="dragMirrorWrapper"
                            style={{
                                transform: `translate(${mirrorPosition.left - dragOffset.left}px, ${mirrorPosition.top - dragOffset.top}px)`,
                                zIndex: mirrorZIndex
                            }}>
                            {/* @ts-ignore - TODO: Revisit here when other elements are migrated to TS */}
                            {h(mirrorComponent, Object.assign({}, dragData, { sourceRect }))}
                        </div>
                    </Portal>
                )}
            </div>
        );
    }
}

export { DragDropContainerProps };
export default DragDropContainer;
